import { PencilSquareIcon } from '@heroicons/react/16/solid';
import { isToday } from 'date-fns';
import { Fragment, useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { usePageContext } from 'vike-react/usePageContext';

import { LoadingIcon } from '@/components/UI/LoadingIcon';
import Skeleton from '@/components/UI/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/UI/table';
import { menuHref } from '@/components/react-aria/menu';
import {
  Pagination,
  PaginationGap,
  PaginationList,
  PaginationNext,
  PaginationPage,
  PaginationPrevious,
} from '@/components/react-aria/pgagination';
import { request } from '@/renderer/utils/api';
import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';
import type { UseInfiniteRequestResult } from '@/renderer/utils/api/types';
import formatReleaseLine from '@/renderer/utils/formatReleaseLine';
import { useLocale } from '@/renderer/utils/locale';
import useMe from '@/renderer/utils/useMe';

import UpdateReleaseForm from './UpdateReleaseForm';

const formatScore = (
  visitorTeam: string | undefined,
  homeTeam: string | undefined,
  visitorScore: number,
  homeScore: number,
  overtimes: number,
) => {
  let score = '';

  if (visitorScore > homeScore) {
    score = `${visitorTeam} ${visitorScore}-${homeScore}`;
  } else if (homeScore > visitorScore) {
    score = `${homeTeam} ${homeScore}-${visitorScore}`;
  } else if (homeScore === visitorScore && homeScore !== null) {
    score = `TIE ${homeScore}-${visitorScore}`;
  }

  if (overtimes > 0) {
    score += `${score ? ' ' : ''}${overtimes > 1 ? overtimes : ''}OT`;
  }

  return score;
};

const formatValue = (
  value: number | null | undefined,
  nullish: string | JSX.Element = '',
  zero: string | JSX.Element | undefined = undefined,
) => {
  if (value === null || value === undefined) {
    return nullish;
  } else if (value > 0) {
    return `+${Number(value)}`;
  } else if (value === 0 && zero !== undefined) {
    return zero;
  }

  return `${Number(value)}`;
};

export default function ResultsTable<
  const UID extends API.Id | 'me' | 'archive',
  const _ResponseType extends UID extends 'archive' ? API.Release : API.MessageReceipt,
>({
  sportId,
  serviceIds,
  playType,
  gameType, // Add this parameter
  startDate,
  endDate,
  userId,
  free,
}: {
  sportId?: number | number[];
  serviceIds?: number[];
  playType?: API.PlayTypes | API.PlayTypes[] | 'full' | 'st' | 'hp' | 'pf';
  gameType?: API.GameTypes[]; // Add this type
  startDate?: Date;
  endDate?: Date;
  userId: UID;
  free?: boolean;
}) {
  const {
    urlParsed: { search },
  } = usePageContext();

  const limit = ALLOWED_LIMITS.includes(+search?.limit) ? +search?.limit : DEFAULT_LIMIT;
  const page = +search?.page || 1;

  const eventValidate = useCallback(
    async (
      _event: MessageEvent,
      ojson: API.SubscriptionResponse<API.MessageReceipt> | API.WebsocketResult<API.Id>,
    ) => {
      const data = ojson.data as API.MessageReceipt;

      if (!data.releaseId) {
        return false;
      }

      if (ojson.message === 'messageReceipt/updated') {
        return {
          ...ojson,
          data: (
            await request<API.WithData<API.MessageReceipt>>(
              `/api/v1/messageReceipt/${userId}/${ojson.data as number}`,
            )
          ).data,
        } satisfies API.SubscriptionResponse<API.MessageReceipt>;
      }
    },
    [userId],
  );

  const releasesRequest = useNextInfiniteRequest<API.Release, void, API.WithPageInput<API.Release>>(
    useMemo(
      () => ({
        key: 'fetchReleases',
        req: `/api/v1/release`,
        query: {
          order: {
            by: 'gameTime',
          },
          page: {
            limit,
            offset: limit * (page - 1),
          },
          data: {
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            playType:
              playType === 'full'
                ? ['side', 'total', 'prop', 'future']
                : playType === 'hp'
                  ? ['side', 'total']
                  : playType === 'st'
                    ? ['side', 'total']
                    : playType === 'pf'
                      ? ['prop', 'future']
                      : playType && typeof playType === 'string'
                        ? [playType]
                        : playType || ['side', 'total', 'prop', 'future'],
            gameType, // Add gameType here
            startDate,
            endDate,
            free,
          },
        },
        skipPoll: true,
        pause: userId !== 'archive',
      }),
      [limit, page, userId, sportId, serviceIds, playType, gameType, startDate, endDate, free], // Add gameType to dependencies
    ),
  );

  const messageReceiptsRequest = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
  >(
    useMemo(
      () => ({
        key: 'fetchMessageReleases',
        req: `/api/v1/messageReceipt/${userId}`,
        event: 'messageReceipt',
        eventValidate,
        query: {
          page: {
            limit,
            offset: limit * (page - 1),
          },
          data: {
            type: 'release',
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            playType:
              playType === 'full'
                ? ['side', 'total', 'prop', 'future']
                : playType === 'hp'
                  ? ['side', 'total']
                  : playType === 'st'
                    ? ['side', 'total']
                    : playType === 'pf'
                      ? ['prop', 'future']
                      : playType && typeof playType === 'string'
                        ? [playType]
                        : playType || ['side', 'total', 'prop', 'future'],
            gameType, // Add gameType here too
            startDate,
            endDate,
            free,
          },
        },
        skipPoll: true,
        pause: userId === 'archive',
      }),
      [
        limit,
        page,
        userId,
        eventValidate,
        sportId,
        serviceIds,
        playType,
        gameType,
        startDate,
        endDate,
        free,
      ], // Add gameType to dependencies
    ),
  );

  return (
    <ResultsTableContent
      userId={userId}
      infiniteRequest={userId === 'archive' ? releasesRequest : messageReceiptsRequest}
    />
  );
}

const ALLOWED_LIMITS = [1, 5, 10, 25, 50, 100];
const DEFAULT_LIMIT = 50;

export function ResultsTableContent<
  const UID extends API.Id | 'me' | 'archive',
  const _ResponseType extends UID extends 'archive' ? API.Release : API.MessageReceipt,
>({
  userId,
  infiniteRequest,
}: {
  userId: UID;
  infiniteRequest:
    | UseInfiniteRequestResult<API.Release, void>
    | UseInfiniteRequestResult<API.MessageReceipt, void>
    | null;
}) {
  const intl = useLocale();
  let lastDate: string;

  const user = useMe();
  const isAdmin = user?.role === 'admin';
  const colSpan = isAdmin ? 7 : 6;

  const columns = useMemo(
    () =>
      [
        { name: 'Sport', showOn: 'medium' },
        { name: 'Play', className: 'w-full' },
        { name: 'Result' },
        { name: 'Score', showOn: 'large' },
        { name: 'Closing line', showOn: 'large' },
        { name: 'CLV', showOn: 'large' },
        ...(isAdmin
          ? [
              {
                name: 'Edit',
              },
            ]
          : []),
      ] as const,
    [isAdmin],
  );

  const {
    isPending,
    error,
    data: results,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
  } = infiniteRequest || {
    isPending: true,
    error: null,
    data: null,
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: false,
  };

  const rows = results?.data || [];
  const hasRows = rows?.length > 0;

  const {
    urlParsed: { pathname, search },
  } = usePageContext();

  const limit = ALLOWED_LIMITS.includes(+search?.limit) ? +search?.limit : DEFAULT_LIMIT;
  const page = +search?.page || 1;

  const perPage = results?.pageInfo.limit || limit || results?.pageInfo.total || 1;
  const pageCount = Math.ceil((results?.pageInfo.total || 0) / perPage) || 1;

  return (
    <>
      <Table className="mt-12 table-auto">
        <TableHeader className="hidden md:contents">
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.name}
                className={twMerge(
                  'sticky top-0 bg-white px-4 py-2',
                  column.showOn === 'medium' && 'hidden md:table-cell',
                  column.showOn === 'large' && 'hidden lg:table-cell',
                  column.className,
                )}
                breakpoint={
                  column.showOn === 'medium' ? 'md' : column.showOn === 'large' ? 'lg' : undefined
                }
              >
                {column.name == 'Edit'
                  ? isAdmin && <span className="sr-only">Edit</span>
                  : column.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {!hasRows &&
            isPending &&
            Array(colSpan)
              .fill(1)
              .map((_, index) => (
                <Fragment key={index}>
                  <TableRow className="border-zinc-900/10 border-b dark:border-white/20">
                    <TableHead
                      colSpan={colSpan}
                      scope="colgroup"
                      className="bg-zinc-50 py-2 pr-3 pl-4 text-left font-normal text-sm text-zinc-900 sm:pl-3 dark:bg-white/5 dark:text-white"
                    >
                      <Skeleton className="inline-block" loading={true}>
                        Safudsday, Jebrugber 32, 2099
                      </Skeleton>
                    </TableHead>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Skeleton className="inline-block" loading={true}>
                        CCC
                      </Skeleton>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="inline-block" loading={true}>
                        000 Sports Team +100 at Sportier Team
                      </Skeleton>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="inline-flex items-center space-x-2">
                        <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-zinc-500/50" />
                        <Skeleton className="inline-block" loading={true}>
                          Pending
                        </Skeleton>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="inline-block" loading={true}>
                        000 - 000
                      </Skeleton>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="inline-block" loading={true}>
                        +000
                      </Skeleton>
                    </TableCell>
                    <Skeleton className="inline-block" loading={true}>
                      000
                    </Skeleton>
                  </TableRow>
                  <TableRow className="border-zinc-900/10 border-b dark:border-white/20">
                    <TableCell>
                      <Skeleton className="inline-block" loading={true}>
                        DDD
                      </Skeleton>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="inline-block" loading={true}>
                        000 Sport Team +100 at Team
                      </Skeleton>
                    </TableCell>
                    <TableCell>
                      <div className="inline-flex items-center space-x-2">
                        <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-zinc-500/50" />
                        <Skeleton className="inline-block" loading={true}>
                          Win
                        </Skeleton>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="inline-block" loading={true}>
                        00 - 00
                      </Skeleton>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="inline-block" loading={true}>
                        +999
                      </Skeleton>
                    </TableCell>
                    <Skeleton className="inline-block" loading={true}>
                      999
                    </Skeleton>
                  </TableRow>
                </Fragment>
              ))}
          {rows?.map?.((row) => {
            const result =
              userId === 'archive' ? (row as API.Release) : (row as API.MessageReceipt).release;
            const nextDate = intl.formatDate(
              // @todo both pages should sort by gameTime
              userId === 'archive' ? result.gameTime || result.createdAt : result.createdAt,
              {
                timeZone: 'America/Chicago', // this needs to be set, or else SSR and CSR will mismatch
                month: 'long',
                day: 'numeric',
                weekday: 'long',
                year: 'numeric',
              },
            );
            const showDate = lastDate !== nextDate;

            lastDate = nextDate;

            let gameDate = undefined;
            let gameDateSmall = undefined;
            let gameDateFull = '';

            if (result.gameTime) {
              if (isToday(new Date(result.gameTime))) {
                gameDate = (
                  <>
                    Today
                    <br />
                    {intl.formatDate(result.gameTime, {
                      month: undefined,
                      day: undefined,
                      weekday: undefined,
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })}
                  </>
                );

                gameDateSmall = (
                  <>
                    Today
                    <br />
                    {intl.formatDate(result.gameTime, {
                      month: undefined,
                      day: undefined,
                      weekday: undefined,
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: undefined,
                    })}
                  </>
                );
              } else {
                gameDate = (
                  <>
                    {intl.formatDate(result.gameTime, {
                      month: 'short',
                      day: 'numeric',
                      weekday: 'short',
                    })}
                    <br />
                    {intl.formatDate(result.gameTime, {
                      month: undefined,
                      day: undefined,
                      weekday: undefined,
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })}
                  </>
                );

                gameDateSmall = (
                  <>
                    {intl.formatDate(result.gameTime, {
                      month: 'short',
                      day: 'numeric',
                      weekday: 'short',
                    })}
                    <br />
                    {intl.formatDate(result.gameTime, {
                      month: undefined,
                      day: undefined,
                      weekday: undefined,
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: undefined,
                    })}
                  </>
                );
              }

              gameDateFull = intl.formatDate(result.gameTime, {
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short',
              });
            }

            return (
              <Fragment key={result.id}>
                {showDate && (
                  <TableRow className="border-zinc-900/10 border-b dark:border-white/20">
                    <TableHead
                      colSpan={colSpan}
                      scope="colgroup"
                      className="bg-zinc-50 py-2 pr-3 pl-4 text-left font-normal text-sm text-zinc-900 sm:pl-3 dark:bg-white/5 dark:text-white"
                    >
                      {nextDate}
                    </TableHead>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell className="whitespace-nowrap text-xs text-zinc-500" breakpoint="md">
                    {result.sport?.shortName}
                  </TableCell>
                  <TableCell>
                    <span>{formatReleaseLine(result, false, true)}</span>
                    {result.gameType === 'fcs' && (
                      <span className="ml-2 rounded-sm bg-blue-400/20 p-1 px-1.5 font-medium text-blue-800 text-xs dark:bg-blue-400/30 dark:text-blue-400">
                        FCS
                      </span>
                    )}
                    {result.gameType === 'extra' && (
                      <span className="ml-2 rounded-sm bg-purple-400/20 p-1 px-1.5 font-medium text-purple-800 text-xs dark:bg-purple-400/30 dark:text-purple-400">
                        Extra
                      </span>
                    )}
                    {!!result.free && (
                      <span className="ml-2 rounded-sm bg-emerald-400/20 p-1 px-1.5 font-medium text-emerald-800 text-xs dark:bg-emerald-400/30 dark:text-emerald-400">
                        Free
                      </span>
                    )}
                    {result.rating !== 1 && (
                      <span className="ml-2 rounded-sm bg-zinc-900/5 p-1 px-1.5 font-medium text-xs dark:bg-white/5">
                        {result.rating} units
                      </span>
                    )}
                    <div className="hidden text-xs text-zinc-500">
                      {result?.sport?.shortName}{' '}
                      {result.playType[0].toUpperCase() + result.playType.substring(1)}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="inline-flex flex-col items-baseline space-x-2">
                      <div className="inline-flex items-center space-x-2">
                        <span
                          className={twMerge(
                            `h-2 w-2 shrink-0 rounded-full bg-zinc-300`,
                            result.result === 'win' && 'bg-emerald-400',
                            result.result === 'loss' && 'bg-rose-600',
                            result.result === 'pending' && 'bg-amber-300',
                          )}
                        />
                        <span>
                          {result.result === 'noAction'
                            ? 'No Action'
                            : result.result[0].toUpperCase() + result.result.substring(1)}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 lg:hidden">
                        <span>
                          {formatScore(
                            result.visitor?.shortName,
                            result.home?.shortName,
                            result.visitorScore,
                            result.homeScore,
                            result.overtimes,
                          )}
                        </span>
                        {result.result === 'pending' && gameDateSmall && (
                          <span
                            className="block text-zinc-400 dark:text-zinc-500"
                            title={gameDateFull}
                          >
                            {gameDateSmall}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-zinc-500" breakpoint="lg">
                    <span className="flex flex-row items-center space-x-1">
                      {result.result !== 'pending' ? (
                        <>
                          {/* {!(result.homeScore === null && result.visitorScore === null) && (
                            <ChevronRightIcon
                              className="ml-0.5 mr-1.5 h-4 w-auto shrink-0"
                              aria-hidden="true"
                            />
                          )} */}
                          <span>
                            {formatScore(
                              result.visitor?.shortName,
                              result.home?.shortName,
                              result.visitorScore,
                              result.homeScore,
                              result.overtimes,
                            )}
                          </span>
                        </>
                      ) : (
                        gameDate && (
                          <span className="text-zinc-400 dark:text-zinc-500" title={gameDateFull}>
                            {gameDate}
                          </span>
                        )
                      )}
                    </span>

                    {result?.resultText}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-zinc-500" breakpoint="lg">
                    {result.closingSide === 0 ? (
                      <span>PK</span>
                    ) : result.closingSide ? (
                      <span>
                        {`${
                          result.sideIndicator === 'visitor'
                            ? result.visitor?.shortName
                            : result.sideIndicator === 'home' && result.home?.shortName
                        } ${formatValue(result.closingSide)}`}
                      </span>
                    ) : (
                      <>
                        {result.closingTotal && result.totalIndicator ? (
                          <span>
                            {`${result.totalIndicator[0].toUpperCase()} ${result.closingTotal}`}
                          </span>
                        ) : result.result !== 'pending' ? (
                          <>&mdash;</>
                        ) : (
                          <></>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-zinc-500" breakpoint="lg">
                    {formatValue(
                      result.closingLineValue,
                      result.result !== 'pending' ? <>&mdash;</> : '',
                      '+0.0',
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="whitespace-nowrap text-emerald-500 text-xs hover:text-emerald-400 dark:text-emerald-500">
                      <UpdateReleaseForm
                        release={result}
                        onSuccessfulSubmit={infiniteRequest?.refetch}
                      >
                        <PencilSquareIcon className="size-4 shrink-0" />
                      </UpdateReleaseForm>
                    </TableCell>
                  )}
                </TableRow>
              </Fragment>
            );
          })}
          {!hasRows && !isPending && (
            <TableRow className="hover:bg-transparent!">
              <TableCell colSpan={colSpan} className="py-8! text-center">
                No Results
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!!error?.message && <>Error: {error.message}</>}

      <section className="w-full select-none">
        <Pagination>
          <PaginationPrevious
            href={
              menuHref(search, {
                page: Math.max(page - 1, 1) === 1 ? undefined : Math.max(page - 1, 1),
              }) || pathname
            }
            isDisabled={page <= 1}
          />
          <PaginationList>
            {pageCount > 5 && page >= 4 && (
              <PaginationPage href={menuHref(search, { page: undefined }) || pathname}>
                1
              </PaginationPage>
            )}
            {pageCount > 6 && page >= 5 && <PaginationGap />}
            {new Array(pageCount < 5 ? pageCount : 5)
              .fill(Math.max(1, Math.min(page - 2, pageCount - 4)))
              .map((start, idx) => {
                const npage = start + idx;
                return (
                  <PaginationPage
                    key={npage}
                    href={menuHref(search, { page: npage === 1 ? undefined : npage }) || pathname}
                    current={page === npage}
                    isLoading={isLoading || isPending}
                  >{`${npage}`}</PaginationPage>
                );
              })}
            {pageCount > 6 && pageCount - page - 2 >= 2 && <PaginationGap />}
            {pageCount > 5 && pageCount - page - 2 >= 1 && (
              <PaginationPage
                href={
                  menuHref(search, { page: pageCount === 1 ? undefined : pageCount }) || pathname
                }
              >{`${pageCount}`}</PaginationPage>
            )}
          </PaginationList>
          <PaginationNext
            href={
              menuHref(search, {
                page:
                  Math.min(page + 1, pageCount) === 1 ? undefined : Math.min(page + 1, pageCount),
              }) || pathname
            }
            isDisabled={page >= pageCount}
          />
        </Pagination>
      </section>
      {hasNextPage && (
        <div className="my-2 w-full">
          <button
            disabled={isFetchingNextPage}
            data-loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            className="cursor-pointer select-none border-0 bg-transparent p-2 text-center text-sm text-zinc-600 hover:text-zinc-500 data-[loading=true]:cursor-progress dark:text-zinc-300 dark:hover:text-zinc-200"
          >
            Load more results
            <LoadingIcon isLoading={isFetchingNextPage} />
          </button>
        </div>
      )}
    </>
  );
}
